import { NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, HostBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Competition } from '@cds/betting-offer/tags';
import { CDNCachingConfig, GridConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { TabGroupWithCounterComponent, TabItemUiViewModel } from '@frontend/sports/common/ui/sports-themed-tab-group';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { ModuleLoaderService } from '@frontend/sports/deferred/feature/module-loader';
import { WidgetsMessages } from '@frontend/sports/types/components/sitecore';
import { Widget, WidgetContext, WidgetType } from '@frontend/sports/types/components/widget';
import {
    ComposableAllTabSettings,
    ComposableWidgetItem,
    ComposableWidgetPayload,
    TabbedGridPayload,
} from '@frontend/sports/types/components/widget/types';
import { PrimaryNavigationDisplayType } from '@frontend/sports/types/configuration';
import { ModularConfigAccessorService } from '@frontend/sports/widget/core/feature';
import { DsTabsModule } from '@frontend/ui/tabsgroup';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { flatMap, uniqBy } from 'lodash-es';
import { Observable, filter, map } from 'rxjs';

import { ShowInEpcotDirective } from '../../common/epcot-config.directive';
import { ErrorComponent } from '../../common/error/error.component';
import { TabBarModule } from '../../tab-bar/tab-bar.module';
import { WidgetCommonModule } from '../common/widget-common.module';
import { WidgetContextService } from '../common/widget-context.service';
import { WidgetNavigationService } from '../common/widget-navigation.service';
import { WidgetPillComponent } from '../common/widget-pill.component';
import { WidgetTabBarMapperRegistryService } from '../common/widget-tab-bar-mapper-registry.service';
import { WidgetTabBarDisplay, WidgetTabBarItem } from '../common/widget-tab-bar.component';
import { WidgetCoreModule } from '../core/widget-core.module';
import { WidgetComponent } from '../core/widget-registry.service';
import { ComposableAccordionsComponent } from './composable-accordions.component';
import { ComposableWidgetTrackingService } from './composable-widget-tracking.service';
import { WidgetChevronClickTrackingData } from './tracking.model';

export interface ComposableParentPayload {
    mergeNavigation: boolean;
    isAllTabEnabled: boolean;
}

export enum ComposableAllTab {
    All = 'All',
}

@Component({
    imports: [
        NgClass,
        ComposableAccordionsComponent,
        WidgetCoreModule,
        WidgetCommonModule,
        TabBarModule,
        CompetitionLogoComponent,
        ErrorComponent,
        ShowInEpcotDirective,
        TrustPipe,
        WidgetPillComponent,
        DsTabsModule,
        TabGroupWithCounterComponent,
        IconCustomComponent,
    ],
    selector: 'ms-composable-widget',
    templateUrl: './composable-widget.component.html',
    providers: [ComposableWidgetTrackingService],
})
export class ComposableWidgetComponent extends WidgetComponent<ComposableWidgetPayload> {
    @HostBinding('class') className = 'composable';
    @HostBinding('class.show-background-color') showBackgroundColor = false;

    childNavigation: WidgetTabBarItem[];
    composableWidget: Widget<ComposableParentPayload>;
    tabsVM?: TabItemUiViewModel[];
    items: ComposableWidgetItem[] = [];
    headerWidgets: Widget<unknown>[] = [];
    widgets: Widget<unknown>[];
    title: string | undefined;
    translations: WidgetsMessages;
    allTabSettings: ComposableAllTabSettings;
    isAllTabActive: boolean;
    hideSecondlevelNavigation: boolean;
    activeItem: ComposableWidgetItem;
    activeItemCompetition: Competition | undefined;
    headerChevronLink: string | undefined;
    private isCDNCachingEnabled = false;
    showPrimaryNavAsTab = false;

    constructor(
        private contextService: WidgetContextService,
        private gridConfig: GridConfig,
        private mapperRegistry: WidgetTabBarMapperRegistryService,
        private trackingService: ComposableWidgetTrackingService,
        private changeRef: ChangeDetectorRef,
        public accessorService: ModularConfigAccessorService,
        private cdnCachingConfig: CDNCachingConfig,
        private moduleLoader: ModuleLoaderService,
        private widgetNavigationService: WidgetNavigationService,
        { widgets: { messages } }: Sitecore,
    ) {
        super();
        this.translations = messages;
        this.isCDNCachingEnabled = this.cdnCachingConfig.widgets.includes(WidgetType.Composable);
        this.showPrimaryNavAsTab = this.gridConfig.primaryNavigationType === PrimaryNavigationDisplayType.Tab;
    }

    override onData(data: ComposableWidgetPayload, isWidgetRefresh: boolean): void {
        if (!data?.items?.length) {
            this.getData(isWidgetRefresh).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.renderData);
        } else {
            this.renderData(data);
        }
    }

    private renderData = (payload: ComposableWidgetPayload) => {
        if (!payload?.items?.length) {
            this.hasData = false;
            this.setWidgetLoaded();
            this.logEmptyData();

            return;
        }

        const childWidgets = uniqBy(
            flatMap(payload.items, (item) => item.children),
            (child) => child.type,
        ).map((child) => child.type);
        this.moduleLoader.lazyLoadFeatures(childWidgets.filter(hasValue));

        this.allTabSettings = payload.allTabSettings;

        this.tabsVM = payload.items?.map((item, index) => ({
            id: index?.toString(),
            title: item.title,
            active: false,
        }));

        const firstItemWithDataIdx = payload.items.findIndex((c) => c.activeChildren);

        if (firstItemWithDataIdx === -1) {
            return;
        }

        this.tabsVM[firstItemWithDataIdx].active = true;
        this.items = payload.items;
        this.activeItem = payload.items[firstItemWithDataIdx];
        this.headerWidgets = payload.headerWidgets;

        this.setData(payload.items[firstItemWithDataIdx], payload.showChevron);
        this.hasData = true;
        this.setWidgetLoaded();
    };

    get singleItemMode(): boolean {
        return this.items.length === 1;
    }

    get getActiveId(): string {
        return this.childNavigation.find((item) => item.active)!.id;
    }

    setData({ title, children, activeChildren, mergeNavigation, showCount }: ComposableWidgetItem, showChevron: boolean = false) {
        this.title = title;
        this.widgets = activeChildren ?? [];
        this.setActiveCompetitionAndChevronLink(showChevron);

        this.childNavigation = children.flatMap((widget) => {
            const mapper = this.mapperRegistry.get(widget.type);

            if (mergeNavigation && mapper) {
                return mapper.map(widget.id, widget.navigation, widget.navigationContext);
            }

            return widget.navigation.map((item) => ({
                id: `${widget.id}-${item.id}`,
                title: item.name,
                context: {},
                widgetId: widget.id,
                itemId: item.id,
                display: WidgetTabBarDisplay.Default,
                count: showCount ? (mapper?.getCount(widget.navigation, widget.navigationContext) ?? item.count) : undefined,
                showSignPost: widget.type === WidgetType.CompetitionPriceBoosts,
            }));
        });

        this.showBackgroundColor = this.allTabSettings.isVisible;
        if (this.allTabSettings.isVisible && this.childNavigation.length > 1) {
            this.hideSecondlevelNavigation = this.allTabSettings.hideSecondaryNavigation;
            this.childNavigation.unshift({
                context: {},
                display: WidgetTabBarDisplay.Default,
                id: `${this.config.id}-${ComposableAllTab.All}`,
                itemId: ComposableAllTab.All,
                title: this.allTabSettings.title,
                widgetId: this.config.id,
                icon: 'sports-all',
            });
        }

        if (this.childNavigation.length > 0) {
            this.childNavigation[0].active = true;
        }

        this.isAllTabActive = this.getAllTabActive();
        this.composableWidget = {
            ...this.config,
            payload: { mergeNavigation, isAllTabEnabled: this.allTabSettings.isVisible },
        };
        this.changeRef.detectChanges();
    }

    composableNavigationSelected(tabName: string): void {
        if (this.tabsVM) {
            const tabIndex = this.tabsVM?.findIndex((tab) => tab.id === tabName?.toString());

            if (tabIndex !== -1) {
                this.setData(this.items[tabIndex]);
                this.activeItem = this.items[tabIndex];

                this.trackingService.trackComposableNavigation(this.tabsVM[tabIndex]);
            }
        }
    }

    override getContext(): Partial<WidgetContext> {
        const context = super.getContext();

        return !this.isCDNCachingEnabled ? context : { ...context, shouldIncludePayload: true };
    }

    private getData(isWidgetRefresh: boolean): Observable<ComposableWidgetPayload> {
        return this.contextService.getWidget<ComposableWidgetPayload>(this.getContext(), !isWidgetRefresh, !this.isCDNCachingEnabled).pipe(
            filter(({ payload }) => payload !== undefined),
            map(({ payload }) => payload as ComposableWidgetPayload),
            takeUntilDestroyed(this.destroyRef),
        );
    }

    childNavigationSelected(item: WidgetTabBarItem): void {
        const tabIndex = this.childNavigation.findIndex((t) => t === item);

        this.contextService
            .getWidget(
                {
                    ...item.context,
                    widgetId: item.widgetId,
                    shouldIncludePayload: true,
                    sportId: item.context.sportId,
                },
                false,
                !this.isCDNCachingEnabled,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((widget) => {
                this.widgets = widget ? [widget] : [];
                if (widget.type === WidgetType.Composable) {
                    const { items } = widget.payload as ComposableWidgetPayload;
                    const activeNavigationIndex = this.tabsVM?.findIndex((navigation) => navigation.active === true) ?? -1;

                    if (activeNavigationIndex === -1) {
                        return;
                    }
                    this.items = items;
                    this.setData(this.items[activeNavigationIndex]);
                }
                this.isAllTabActive = this.getAllTabActive();
                this.trackingService.trackChildNavigation(widget, item.context, item.itemId?.toString(), tabIndex);
                this.changeRef.detectChanges();
            });
    }

    trackLinkClick({
        widgetId,
        trackingData,
        widgetContext,
        itemId,
        details,
        url,
    }: {
        widgetId: string;
        widgetContext: Partial<WidgetContext>;
        itemId: string;
        details: string;
        trackingData?: Record<string, string>;
        url?: string;
    }) {
        const { templateName, type } = this.activeItem.children.find((x) => x.id === widgetId)!;
        this.trackingService.trackLinkClick(templateName, type, widgetContext, itemId, details, trackingData, url);
    }

    trackAccordionClick({ widget, widgetContext, itemId }: { widget: Widget<unknown>; widgetContext: Partial<WidgetContext>; itemId: string }) {
        this.trackingService.trackAccordionClick(widget, widgetContext, itemId);
    }

    headerNavigationHandler(): void {
        this.widgetNavigationService.headerNavigationHandler(this.headerChevronLink);
        this.headerChevronTracking();
    }

    private headerChevronTracking(): void {
        const data: WidgetChevronClickTrackingData = {
            eventDetails: this.getTrackingEventDetails(),
            urlClicked: this.headerChevronLink ?? '',
        };
        this.trackingService.trackHeaderChevronClick(data);
    }

    private getTrackingEventDetails(): string {
        const league =
            this.singleItemMode && this.widgets[0]?.type === WidgetType.TabbedGrid
                ? (this.widgets[0]?.payload as TabbedGridPayload)?.fixtures?.[0].competition.name.value
                : '';
        return league ? `${league}-chevron icon` : '';
    }

    private getActiveChildNavigation(): WidgetTabBarItem | undefined {
        return this.childNavigation.find((child) => child.active);
    }

    private getAllTabActive(): boolean {
        const activeChildNavigation = this.getActiveChildNavigation();
        if (activeChildNavigation && this.allTabSettings.isVisible) {
            return activeChildNavigation.itemId === ComposableAllTab.All || this.childNavigation.length === 1;
        }

        return false;
    }

    private setActiveCompetitionAndChevronLink(showChevron: boolean): void {
        if (this.singleItemMode && this.widgets[0]?.type === WidgetType.TabbedGrid) {
            const payload = this.widgets[0]?.payload as TabbedGridPayload;
            const fixture = payload?.fixtures?.[0];
            if (!fixture) {
                return;
            }
            this.activeItemCompetition = fixture.competition;
            const sportUrlParam = { id: fixture.sport.id, name: fixture.sport.name.value };
            this.headerChevronLink = showChevron
                ? this.widgetNavigationService.getNavigationInfo(sportUrlParam, payload?.bottomLink, fixture)?.link
                : undefined;
        }
    }
}
